import { faCaretDown, faCaretRight, faChartLine, faCircle, faClose, faExclamationCircle, faMinus, faRocket, faSpinner, faWaveSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useResizeObserver from '@react-hook/resize-observer';
import * as hdf5 from 'jsfive';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, ButtonGroup, Card, Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useLocation, useParams } from 'react-router-dom';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { callFhirMedications } from '../../amplify/appsync/fetch-sync-fhir';
import { ANNOTATIONS_BY_CSN, ANNOTATIONS_EXTENSIONS_BY_CSN, ARCHIVED_DATA, ARCHIVED_WAVEFORMS, Annotation, FAVORITE_LISTS_FOR_USERNAME, Favorite, GET_VISIT_METADATA, LABS_BY_CSN, Lab, MEDICATIONS_BY_CSN, Medication, NUMERICS_BY_CSN, PAST_NUMERICS_REFERENCE_RANGE_BY_MRN, RadiologyReport, callQuery } from '../../amplify/appsync/query';
import { PatientVisit } from '../../amplify/appsync/search-patients';
import { INSERT_RECENT, callWrite } from '../../amplify/appsync/write';
import { readBinaryFile } from '../../amplify/s3/storage';
import { AppAuthStateProps } from '../../types/propTypes';
import { isDemoUser } from '../../utils/demo';
import { capWaveformVals, debounce, generateTimestamps, isLastVisibleNumericsPlot, off, on, verticallySeparatePoints } from '../../utils/plot';
import { getCurrMinMax } from '../../utils/scales';
import { getStorage, setStorage } from '../../utils/storage';
import { getDatePath, getEarliestTime, getLatestTime, getMinuteTruncatedEpoch, getRelativeDate, getSqlDateStrFromDateStr } from '../../utils/time';
import { getUserEmail } from '../../utils/user';
import { getNormalVitalSignRange } from '../../utils/vital-signs';
import { FavoriteContextMenu } from '../cohort/FavoriteContextMenu';
import { FavoriteNewListModal } from '../cohort/FavoriteNewListModal';
import useSetPagePatientVisit from '../common/GlobalOptionsContext';
import { FILTER_PERIOD_OPTIONS, FILTER_REFERENCE_RANGE_OPTIONS, FILTER_STATISTIC_OPTIONS } from "../filters/DashboardFilterOptions";
import { WaveformAnnotationSingleSelectMenu } from '../filters/WaveformAnnotationSingleSelectMenu';
import { convertRadiologyReportToAnnotation, parseDataForTimelinePlot, parseECGNumerics, parseLabsDataForModal, parseUniqueECGClassifications, parseWaveformAnnotations } from './DashboardDataParser';
import { PREFERRED_ECG_NUMERICS_ORDER, PREFERRED_NUMERICS_ORDER, PREFERRED_WAVEFORMS_ORDER, SelectedTimelineMinMax, convertToTempUnit, getAvailableNumerics, getAvailableWaveforms, getECGNumericOpts, getNumericOpts, getTimelineOpts, getWaveformAvailabilityOpts, getWaveformOpts } from './DashboardOpts';
import ECGRhythmTimeline from './ECGRhythmTimeline';
import { FilterBar } from './FilterBar';
import { LabsModal } from './Labs';
import { MonitorDashboard } from './llm/notes/MonitorDashboard';
import { LoadingPatient } from './LoadingPatient';
import { MedsModal } from './Meds';
import { OrdersModal } from './Orders';
import { OtherAnnotationModal } from './OtherAnnotationModal';
import { RiskBar } from './risk/RiskBar';
import TimelineContextMenu from './TimelineContextMenu';
import UplotReact from './UplotReact';
import UserAnnotationModal from './UserAnnotationModal';
import { Welcome } from './Welcome';

declare global {
    var xAnnotationMarksForNumerics: any[];
    var xAnnotationMarksForWaveforms: any[];
    var xAnnotationMarksForFeatureImportance: any[];
}

globalThis.xAnnotationMarksForNumerics = [];
globalThis.xAnnotationMarksForWaveforms = [];
globalThis.xAnnotationMarksForFeatureImportance = [];


interface DashboardProps extends AppAuthStateProps {
    patientVisit?: PatientVisit;
}


export const Dashboard = (props: DashboardProps) => {
    let { internalCSN } = useParams();
    const location = useLocation();
    const queryModel = queryString.parse(location.search);
    const patientVisit = props.patientVisit;

    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);

    const [featureImportance, setFeatureImportance] = useState<any>({});
    const [timestamps, setTimestamps] = useState<any>({});

    const [filterOptions, setFilterOptions]: any = useState<any>({
        "numerics": queryModel["numerics"] ? (queryModel["numerics"] as string).split(",") : [],
        "waveforms": queryModel["waveforms"] ? (queryModel["waveforms"] as string).split(",") : [],
        "period": queryModel["period"] ? queryModel["period"] : 1,
        "statistic": queryModel["statistic"] ? queryModel["statistic"] : 0,
        "reference": queryModel["reference"] ? queryModel["reference"] : 0,
        "annotations": queryModel["annotations"] ? (queryModel["annotations"] as string).split(",") : [],
    });
    const [filterWaveformOptions, setFilterWaveformOptions]: any = useState<any>({
        "waveformAnnotation": queryModel["waveformAnnotation"] ? queryModel["waveformAnnotation"] : 0,
    });
    const [filterWaveformAnnotationShow, setFilterWaveformAnnotationShow] = useState<any>(false);

    const [overviewData, setOverviewData] = useState<any>({});
    const [numericsData, setNumericsData] = useState<any>({});
    const [numericsRefRangeData, setNumericsRefRangeData] = useState<any>({});
    const [numericsHidden, setNumericsHidden] = useState<any>({});
    const numericsHiddenRef = useRef<any>();
    const [labsData, setLabsData] = useState<Lab[][]>([]);
    const [medsData, setMedsData] = useState<Medication[]>([]);
    const [tempUnit, setTempUnit] = useState<string>('C');
    const [lockTimelineZoom, setLockTimelineZoom] = useState<boolean>(false);

    const [dispoAdmitRisk, setDispoAdmitRisk] = useState<any>(null);
    const [dispoAdmitRiskTrend, setDispoAdmitRiskTrend] = useState<any>([[], []]);

    const [waveformAvailabilityData, setWaveformAvailabilityData] = useState<any>([[], [], [], [], []]);
    const [waveformNumericsData, setWaveformNumericsData] = useState<any>({});
    const [waveformsData, setWaveformsData] = useState<any>({});

    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoadingOverview, setIsLoadingOverview] = useState<any>(false);
    const [isLoadingNumerics, setIsLoadingNumerics] = useState<any>(false);
    const [isLoadingAnnotations, setIsLoadingAnnotations] = useState<boolean>(false);
    const [isLoadingWaveforms, setIsLoadingWaveforms] = useState<any>({});

    const [waveformAvailabilityOpts, setWaveformAvailabilityOpts] = useState<any>(null);
    const [waveformOpts, setWaveformOpts] = useState<any>([]);
    const [uniqueECGClassifications, setUniqueECGClassifications] = useState<any>([]);
    const [timelineOpts, setTimelineOpts] = useState<any>(null);
    const [numericOpts, setNumericOpts] = useState<any>([]);
    const [waveformNumericsOpts, setWaveformNumericOpts] = useState<any>([]);

    // Shows the custom annotation modal popup
    const [showContextMenu, setShowContextMenu] = useState<any>(false);
    const [contextMenuPos, setContextMenuPos] = useState<any>({ x: 0, y: 0 });
    const [showUserAnnotationModal, setShowUserAnnotationModal] = useState<any>(false);
    const [userAnnotationHighlightedTime, setUserAnnotationHighlightedTime] = useState<any>(null);
    const [userAnnotations, setUserAnnotations] = useState<any>([]);

    const [showFavoriteContextMenu, setShowFavoriteContextMenu] = useState<boolean>(false);
    const [favoriteContextMenu, setFavoriteContextMenu] = useState<any>({x: 0, y: 0, csn: null});
    const [showNewList, setShowNewList] = useState<boolean>(false);
    const [favoriteInfo, setFavoriteInfo] = useState<any[]>([]);
    const [favoriteLists, setFavoriteLists] = useState<string[]>([]);

    const [showOtherAnnotationsModal, setShowOtherAnnotationsModal] = useState<any>(false);
    const [selectedOtherAnnotations, setSelectedOtherAnnotations] = useState<Annotation[] | null>(null);
    const [otherAnnotations, setOtherAnnotations] = useState<any>([]);

    // Shows the labs modal popup
    const [showLabsModal, setShowLabsModal] = useState<any>(false);
    // If set, highlights the labs corresponding to this particular time in the labs modal
    const [labsModalHighlightedTime, setLabsModalHighlightedTime] = useState<any>(null);
    const [showMedsModal, setShowMedsModal] = useState<any>(false);
    const [showOrdersModal, setShowOrdersModal] = useState<any>(false);
    const [showDispoRiskModal, setShowDispoRiskModal] = useState<any>(false);
    const [lastUpdatedTime, setLastUpdatedTime] = useState<any>(null);

    const [highlightedEvents, setHighlightedEvents] = useState<any>([]);
    const [selectedTimelineMinMax, setSelectedTimelineMinMax] = useState<SelectedTimelineMinMax>({});

    const target: any = React.useRef(null)
    const timelinePlotRef: any = React.useRef(null)
    const [timelinePlotContainerHeight, setTimelinePlotContainerHeight] = React.useState(180);
    const timelinePlotContainerRef: any = React.createRef();

    const waveformPlotRef: any = React.useRef([]);

    const [plotWidth, setPlotWidth] = React.useState(1024);
    const plotWidthRef = useRef(plotWidth);

    const [highlightRange, setHighlightRange] = useState(null);

    const doc = document;

    //----------------

    let x0: any;
    let lft0: any;
    let wid0: any;

    const lftWid = { left: null, width: null };

    // Sets the patient context for the Assistant
    //
    useSetPagePatientVisit(patientVisit);

    // Run on the initial load to size the graphs properly
    //
    React.useLayoutEffect(() => {
        if (target.current) {
            const width = target.current.getBoundingClientRect().width;
            setPlotWidth(width);

            if (patientVisit) {
                setTimelineOpts((prevOpt: any) => getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, plotWidth, timelinePlotContainerHeight, patientVisit, setSelectedTimelineMinMax, lockTimelineZoom, props.tenant));
                setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, width, patientVisit));

                let minMax = getCurrMinMax(patientVisit, props.tenant);
                setNumericOpts((prevNumerics: any) => {
                    const numerics = getAvailableNumerics(patientVisit, filterOptions);
                    return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, width, minMax, updateWaveformPreview, patientVisit,
                        isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)))
                });
                setWaveformNumericOpts(
                    PREFERRED_ECG_NUMERICS_ORDER.map((numeric: any, idx: number) => getECGNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, width, minMax, updateWaveformPreview, patientVisit, true))
                );
                setWaveformOpts(getAvailableWaveforms(patientVisit, filterOptions).map((waveform: any) => getWaveformOpts(waveform, width)));
            }
        }
    }, [target]);

    useEffect(() => {
        plotWidthRef.current = plotWidth;
    }, [plotWidth]);

    // Run on subsequent iterations page width changes
    //
    useResizeObserver(target, (entry: any) => {
        let resizeTo = entry.contentRect.width - 60;
        triggerResize(resizeTo);
    })

    const triggerResize = (resizeTo: number) => {
        setPlotWidth(resizeTo)

        const width = resizeTo;
        refreshPlots(width);
    }

    const refreshPlots = (width: number, lockTimelineZoomOverride?: boolean) => {
        if (patientVisit) {
            setTimelineOpts(getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, width, timelinePlotContainerHeight, patientVisit, setSelectedTimelineMinMax, lockTimelineZoomOverride !== undefined ? lockTimelineZoomOverride : lockTimelineZoom, props.tenant));
            setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, width, patientVisit));
            let minMax = getCurrMinMax(patientVisit, props.tenant);
            setNumericOpts((prevNumerics: any) => {
                const numerics = getAvailableNumerics(patientVisit, filterOptions);
                return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, width, minMax, updateWaveformPreview, patientVisit,
                    isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)))
            });
            setWaveformNumericOpts(
                PREFERRED_ECG_NUMERICS_ORDER.map((numeric: any, idx: number) => getECGNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, width, minMax, updateWaveformPreview, patientVisit, true))
            );
            setWaveformOpts(getAvailableWaveforms(patientVisit, filterOptions).map((waveform: any) => getWaveformOpts(waveform, width)));
        }
    }

    function resetZoom(currPatientVisit?: PatientVisit, lockTimelineZoomOverride?: boolean) {
        console.log("Reset zoom outer...")

        let pt = currPatientVisit ? currPatientVisit : patientVisit;
        if (pt) {
            console.log("Reset zoom...")

            // Clear the zoomed range, which is a global variable that is set whenever there
            // is an active annotation. This is required as adding annotations will inherently
            // cause the plot to redraw, which will reset the zoom.
            // @ts-ignore
            globalThis.zoomedRange = {};
            setSelectedTimelineMinMax({});
            let minMax = {
                min: getEarliestTime(pt) / 1000,
                max: getLatestTime(pt, props.tenant) / 1000,
            }

            setTimelineOpts(getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, timelineOpts.width, timelinePlotContainerHeight, pt, setSelectedTimelineMinMax, lockTimelineZoomOverride !== undefined ? lockTimelineZoomOverride : lockTimelineZoom, props.tenant));

            setNumericOpts(function (prevNumerics: any) {
                const numerics = getAvailableNumerics(pt!, filterOptions);
                return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, timelineOpts.width, minMax, updateWaveformPreview, pt,
                    isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)))
            }.bind({ pt: pt }));

            setWaveformNumericOpts(
                PREFERRED_ECG_NUMERICS_ORDER.map((numeric: any, idx: number) => getECGNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, timelineOpts.width, minMax, updateWaveformPreview, patientVisit, true))
            );
        }
    }
    function update(uRanger: uPlot, newLft: any, newWid: any) {
        let newRgt = newLft + newWid;
        let maxRgt = uRanger.bbox.width / devicePixelRatio;

        if (patientVisit && newWid > 0) {
            // This function only applies if there is a highlighted box already

            if (newLft >= 0 && newRgt <= maxRgt) {
                select(uRanger, newLft, newWid);
                let minMax = {
                    min: uRanger.posToVal(newLft, 'x'),
                    max: uRanger.posToVal(newLft + newWid, 'x'),
                }
                // @ts-ignore
                globalThis.zoomedRange = minMax;
                setNumericOpts((prevNumerics: any) => {
                    const numerics = getAvailableNumerics(patientVisit, filterOptions);
                    return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit,
                        isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)));
                });
                setWaveformNumericOpts(
                    PREFERRED_ECG_NUMERICS_ORDER.map((numeric: any, idx: number) => getECGNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit, true))
                );
            }
        }
    }

    function select(uRanger: uPlot, newLft: any, newWid: any) {
        lftWid.left = newLft;
        lftWid.width = newWid;
        // @ts-ignore
        uRanger.setSelect(lftWid, false);
    }

    function bindMove(uRanger: uPlot, e: any, onMove: any) {
        x0 = e.clientX;
        lft0 = uRanger.select.left;
        wid0 = uRanger.select.width;

        const _onMove = debounce(onMove);
        on("mousemove", doc, _onMove);

        const _onUp = (e: any) => {
            off("mouseup", doc, _onUp);
            off("mousemove", doc, _onMove);
            // viaGrip = false;
        };
        on("mouseup", doc, _onUp);

        e.stopPropagation();
    }

    //----------------

    useEffect(() => {
        if (patientVisit) {
            globalThis.xAnnotationMarksForNumerics = [];
            globalThis.xAnnotationMarksForWaveforms = [];
            globalThis.xAnnotationMarksForFeatureImportance = [];

            if (!queryModel["numerics"] || !queryModel["waveforms"]) {
                const updatedFilterOptions = {
                    ...filterOptions,
                }
                // User has not previously specified which specific numerics or waveforms they want to see
                // In this case, we will show them all available numerics or waveforms
                if (!queryModel["numerics"]) {
                    // Do not display NBPm by default
                    updatedFilterOptions["numerics"] = PREFERRED_NUMERICS_ORDER.filter((numeric: string) => (numeric !== "NBPm") && (numeric !== "NBPs") && (numeric !== "NBPd"));
                }
                if (!queryModel["waveforms"]) {
                    updatedFilterOptions["waveforms"] = PREFERRED_WAVEFORMS_ORDER;
                }
                setFilterOptions(updatedFilterOptions);
            }
            fetchNumerics(patientVisit);
            fetchWaveforms(patientVisit);
            fetchChartAndAnnotations(patientVisit);
            if (!isFirstLoad) {
                // We don't need to reset zoom on the first load, but will need to reset
                // the x-scales of the plots if the patient changes.
                resetZoom(patientVisit);
            }
            setIsFirstLoad(false);

            // Records this visit as a recent visit
            callWrite({
                operation: INSERT_RECENT,
                favorite_time: new Date().getTime(),
                favorite_type: "recent",
                csn: patientVisit.csn,
                mrn: patientVisit.mrn,
                internal_csn: patientVisit.internal_csn,
                internal_mrn: patientVisit.internal_mrn,
            });

            callQuery({
                operation: GET_VISIT_METADATA,
                internal_csn: internalCSN,
                username: getUserEmail(props.user),
            })
            .then((metadata: any | null) => {
                if (metadata) {
                    const delay = metadata.delay;
                    if (delay) {
                        setLastUpdatedTime(new Date(Date.now() - delay * 1000));
                    } else {
                        setLastUpdatedTime(null);
                    }
                }

                if (metadata && metadata.favorite) {
                    setFavoriteInfo(metadata.favorite);
                }
            });
        }
    }, [patientVisit]);

    useEffect(() => {
        console.log("Filters updated, rerunning query");
        if (patientVisit) {
            fetchNumerics(patientVisit);
            fetchWaveforms(patientVisit);
        }
    }, [filterOptions]);

    const updateFilterOptions = (option: string, val: any) => {
        setFilterOptions({
            ...filterOptions,
            [option]: val,
        });
    }

    useEffect(() => {
        console.log("Waveform filters updated, rerunning query");
        if (patientVisit) {
            if (patientVisit.archived !== "SCHEMA_V1") {
                fetchAnnotations(patientVisit);
            }
        }
    }, [filterWaveformOptions]);

    const updateFilterWaveformOptions = (option: string, val: any) => {
        setFilterWaveformOptions({
            ...filterOptions,
            [option]: val,
        });
    }

    const fetchChartAndAnnotations = (currPatientVisit: PatientVisit) => {
        console.log("Calling fetchChartAndAnnotations...");
        setIsLoadingOverview(true);

        const labsPromise = callQuery({
            operation: LABS_BY_CSN,
            csn: currPatientVisit.csn,
        });
        let medsPromise;
        if (currPatientVisit.departure_time || props.tenant === "public") {
            // If the patient has left already, then we rely on the medications
            // stored in the database because the medications are synced once
            // the patient leaves (MAR is an expensive call).
            medsPromise = callQuery({
                operation: MEDICATIONS_BY_CSN,
                csn: currPatientVisit.csn,
            });
        } else {
            // If the patient is still active, then we rely on the medications
            // directly from FHIR.
            medsPromise = callFhirMedications({
                csn: currPatientVisit.csn,
                mrn: currPatientVisit.mrn,
                external_id: currPatientVisit.external_id!,
            });
        }

        setIsLoadingAnnotations(true);
        const annotationsExtensionsPromise = callQuery({
            operation: ANNOTATIONS_EXTENSIONS_BY_CSN,
            csn: currPatientVisit.csn,
        });

        // Load all of the data that feeds into the timeline chart
        Promise.all([labsPromise, medsPromise, annotationsExtensionsPromise]).then((values) => {
            const labs: Lab[] = values[0];
            const meds: Medication[] = values[1];
            const annotations: Annotation[] = values[2]["annotations"];
            const radiologyReports: RadiologyReport[] = values[2]["radiology_reports"];
            annotations.push(...convertRadiologyReportToAnnotation(radiologyReports));

            // Process the annotations
            const waveformAvailability = parseWaveformAnnotations(annotations, currPatientVisit, uniqueECGClassifications, filterWaveformOptions, props.tenant);
            setUserAnnotations([...annotations.filter(annotation => annotation.annotation_source === "USER_ANNOTATION")])
            setOtherAnnotations([...annotations.filter(annotation => annotation.annotation_source === "RADIOLOGY_REPORT")])
            if (currPatientVisit.archived !== "SCHEMA_V1") {
                // V1 schema patient visits don't have any waveform availability data in the database
                setWaveformAvailabilityData(waveformAvailability);
            }
            setIsLoadingAnnotations(false);
            const classifications = parseUniqueECGClassifications(annotations, filterWaveformOptions);
            setUniqueECGClassifications(classifications);

            // Process the annotations for the ECG numerics (like QTc intervals)
            setWaveformNumericsData(parseECGNumerics(annotations, currPatientVisit));

            // Process the labs and medications
            const plotData = parseDataForTimelinePlot(currPatientVisit, labs, meds, annotations, props.tenant);
            setIsLoadingOverview(false);
            setOverviewData(plotData);
            setTimelineOpts(getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, plotWidth, timelinePlotContainerHeight, currPatientVisit, setSelectedTimelineMinMax, lockTimelineZoom, props.tenant));

            setLabsData(parseLabsDataForModal(labs));
            setMedsData(meds);
        });
    }

    const fetchAnnotations = (currPatientVisit: PatientVisit) => {
        console.log("Calling fetchAnnotations...");
        setIsLoadingAnnotations(true);

        const annotationsPromise = callQuery({
            operation: ANNOTATIONS_BY_CSN,
            csn: currPatientVisit.csn,
        });

        Promise.all([annotationsPromise]).then((values) => {
            console.log("fetchAnnotations promise returned...");

            const annotations: Annotation[] = values[0];
            const waveformAvailability = parseWaveformAnnotations(annotations, currPatientVisit, uniqueECGClassifications, filterWaveformOptions, props.tenant);

            if (currPatientVisit.archived !== "SCHEMA_V1") {
                setWaveformAvailabilityData(waveformAvailability);
            }
            setIsLoadingAnnotations(false);

            const classifications = parseUniqueECGClassifications(annotations, filterWaveformOptions);
            setUniqueECGClassifications(classifications);
        });
    }

    const fetchNumerics = (currPatientVisit: PatientVisit) => {
        setIsLoadingNumerics(true);

        let numericsPromise;
        if (currPatientVisit.archived === "SCHEMA_V1") {
            numericsPromise = callQuery({
                operation: ARCHIVED_DATA,
                csn: currPatientVisit.csn,
                start: getSqlDateStrFromDateStr(getEarliestTime(currPatientVisit)),
                end: getSqlDateStrFromDateStr(getLatestTime(currPatientVisit, props.tenant)),
                period: FILTER_PERIOD_OPTIONS[filterOptions["period"]].id,
                statistic: FILTER_STATISTIC_OPTIONS[filterOptions["statistic"]].id,
                modalities: getAvailableNumerics(currPatientVisit, filterOptions),
            });
        } else {
            numericsPromise = callQuery({
                operation: NUMERICS_BY_CSN,
                csn: currPatientVisit.csn,
                start: getSqlDateStrFromDateStr(getEarliestTime(currPatientVisit)),
                end: getSqlDateStrFromDateStr(getLatestTime(currPatientVisit, props.tenant)),
                period: FILTER_PERIOD_OPTIONS[filterOptions["period"]].id,
                statistic: FILTER_STATISTIC_OPTIONS[filterOptions["statistic"]].id,
                modalities: getAvailableNumerics(currPatientVisit, filterOptions),
                data_granularity: currPatientVisit.granularity,
            });
        }

        const promises = [numericsPromise];

        if (FILTER_REFERENCE_RANGE_OPTIONS[filterOptions["reference"]].id === "individual") {
            const referenceRangePromise = callQuery({
                operation: PAST_NUMERICS_REFERENCE_RANGE_BY_MRN,
                mrn: currPatientVisit.mrn,
                lower_percentile: 0.05,
                upper_percentile: 0.95,
            });
            promises.push(referenceRangePromise);
        } else if (FILTER_REFERENCE_RANGE_OPTIONS[filterOptions["reference"]].id === "normal") {
            const referenceRangePromise = getNormalVitalSignRange(currPatientVisit.age, currPatientVisit.gender);
            promises.push(referenceRangePromise);
        }

        Promise.all(promises).then((promiseOutput: any) => {

            const data = promiseOutput[0];
            let referenceRange: any = null;
            if (promises.length > 1) {
                referenceRange = promiseOutput[1][0];
            }
            if (currPatientVisit.archived === "SCHEMA_V1") {
                // console.log("ARCHIVED", data)
                if (data && data["numerics"]) {
                    processNumericsResponse(currPatientVisit, data["numerics"], referenceRange);
                    const waveformAvailability = parseWaveformAnnotations(data["annotations"], currPatientVisit, uniqueECGClassifications, filterWaveformOptions, props.tenant);

                    setWaveformAvailabilityData(waveformAvailability);
                } else {
                    processNumericsResponse(currPatientVisit, {}, referenceRange);
                    const waveformAvailability = parseWaveformAnnotations([], currPatientVisit, uniqueECGClassifications, filterWaveformOptions, props.tenant);

                    setWaveformAvailabilityData(waveformAvailability);
                }
            } else {
                processNumericsResponse(currPatientVisit, data, referenceRange);
            }
        });
    }

    const processNumericsResponse = (currPatientVisit: PatientVisit, items: any, referenceRange?: any) => {

        const refRangeText: any = {};

        // items:
        // {
        //      "HR": [
        //          [1600883520, 1600883521, ...],
        //          [1, 2, ...], // Charted #1
        //          [1, 2, ...], // Charted #2
        //          [1, 2, ...], // Monitor #1
        //          [1, 2, ...], // Monitor #2
        //      ]
        // }

        // Clear the zoomed range, which is a global variable that is set whenever there
        // is an active annotation. This is required as adding annotations will inherently
        // cause the plot to redraw, which will reset the zoom.
        // @ts-ignore
        globalThis.zoomedRange = {};
        setSelectedTimelineMinMax({});

        for (let key of [...Object.keys(items)]) {
            // This ensures that the min/max bounds are respected when the zoom is reset
            items[key][0].unshift(getEarliestTime(currPatientVisit) / 1000);
            for (let i = 1; i < items[key].length; i++) {
                items[key][i].unshift(null);
            }

            items[key][0].push(getLatestTime(currPatientVisit, props.tenant) / 1000);
            for (let i = 1; i < items[key].length; i++) {
                items[key][i].push(null);
            }

            // Add a third set of data element to indicate the reference ranges
            // items:
            // {
            //      "HR": [
            //          [1600883520, 1600883521, ...],
            //          [1, 2, ...], // Charted #1
            //          [1, 2, ...], // Charted #2
            //          [1, 2, ...], // Monitor #1
            //          [1, 2, ...], // Monitor #2
            //          [1, 2, ...], // ABNORMAL Charted #1
            //          [1, 2, ...], // ABNORMAL Charted #2
            //          [1, 2, ...], // ABNORMAL Monitor #1
            //          [1, 2, ...], // ABNORMAL Monitor #2
            //      ]
            // }
            items[key].push(new Array(items[key][0].length).fill(null))
            items[key].push(new Array(items[key][0].length).fill(null))
            items[key].push(new Array(items[key][0].length).fill(null))
            items[key].push(new Array(items[key][0].length).fill(null))

            if (referenceRange) {

                if (key === "NBP") {
                    const nbpsLower = Math.round(referenceRange[`nbps_lower`] * 100) / 100;
                    const nbpsUpper = Math.round(referenceRange[`nbps_upper`] * 100) / 100;
                    const nbpdLower = Math.round(referenceRange[`nbpd_lower`] * 100) / 100;
                    const nbpdUpper = Math.round(referenceRange[`nbpd_upper`] * 100) / 100;
                    // newYAnnotationMarks[key] = [
                    //     {
                    //         type: key,
                    //         from: nbpsLower,
                    //         to: nbpsUpper,
                    //         label: `Systolic Reference Range: [${nbpsLower}-${nbpsUpper}]`,
                    //     },
                    //     {
                    //         type: key,
                    //         from: nbpdLower,
                    //         to: nbpdUpper,
                    //         label: `Diastolic Reference Range: [${nbpdLower}-${nbpdUpper}]`,
                    //     }
                    // ];

                    refRangeText[key] = `Systolic/Diastolic Reference Range: [${nbpsLower} - ${nbpsUpper}]/[${nbpdLower} - ${nbpdUpper}]`

                    for (let i = 1; i < 5; i += 2) {
                        items[key][i].forEach((element: any, k: number) => {
                            if (element && (element < nbpsLower || element > nbpsUpper)) {
                                items[key][i + 4][k] = element;
                            }
                        });
                    }
                    for (let i = 2; i < 5; i += 2) {
                        items[key][i].forEach((element: any, k: number) => {
                            if (element && (element < nbpdLower || element > nbpdUpper)) {
                                items[key][i + 4][k] = element;
                            }
                        });
                    }
                } else {
                    const lower = Math.round(referenceRange[`${key.toLowerCase()}_lower`] * 100) / 100;
                    const upper = Math.round(referenceRange[`${key.toLowerCase()}_upper`] * 100) / 100;

                    refRangeText[key] = `${key} Reference Range: [${lower} - ${upper}]`

                    for (let i = 1; i < 5; i++) {
                        items[key][i].forEach((element: any, k: number) => {
                            if (element && (element < lower || element > upper)) {
                                items[key][i + 4][k] = element;
                            }
                        });
                    }
                }

                // newYAnnotationMarks[key] = [{
                //     type: key,
                //     from: lower,
                //     to: upper,
                //     label: `${key} Reference Range: [${lower}-${upper}]`,
                // }];
            }
        }
        setNumericsData(items);
        setNumericsRefRangeData(refRangeText);

        // Note we add a custom object to the window so that we can later access the numerics data
        // when displaying the tooltips
        // @ts-ignore
        window.numericsData = items;

        let hiddenNumerics: any = {};
        getAvailableNumerics(currPatientVisit, filterOptions).forEach((numeric: string, idx: number) => {
            if (!items[numeric] || items[numeric][0].length === 0) {
                hiddenNumerics[numeric] = true;
            }
        });

        if (getStorage(`numericsHidden_${internalCSN}`)) {
            const localStorageNumericsHidden = JSON.parse(getStorage(`numericsHidden_${internalCSN}`)!);
            for (let k in localStorageNumericsHidden) {
                if (localStorageNumericsHidden[k]) {
                    hiddenNumerics[k] = true;
                }
            }
        }
        setNumericsHidden({ ...hiddenNumerics });
        numericsHiddenRef.current = { ...hiddenNumerics };

        let minMax = getCurrMinMax(currPatientVisit, props.tenant);
        setNumericOpts((prevNumerics: any) => {
            const numerics = getAvailableNumerics(currPatientVisit, filterOptions);
            return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, currPatientVisit,
                isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)))
        });
        setWaveformNumericOpts(
            PREFERRED_ECG_NUMERICS_ORDER.map((numeric: any, idx: number) => getECGNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit, true))
        );

        setIsLoadingNumerics(false);
    }

    const handleNumericsHiddenChange = (hiddenNumerics: any) => {
        setStorage(`numericsHidden_${internalCSN}`, JSON.stringify(hiddenNumerics));
        setNumericsHidden(hiddenNumerics);
    }

    const updateCsnToFavorites = (csn: string, favoriteType: string, favorite?: Favorite) => {
        console.log("updateCsnToFavorites", favoriteType)
        let map: any = [...favoriteInfo];
        if (favorite) {
            // newly saved favorite
            map = map.filter((v: Favorite) => v.favorite_type !== favorite.favorite_type);
            map.push(favorite);
        } else {
            // favorite deleted
            map = map.filter((v: Favorite) => {
                console.log("Filtering...", v, v.favorite_type , favoriteType, v.favorite_type !== favoriteType)
                return v.favorite_type !== favoriteType
            });
            
            console.log("updateCsnToFavorites deleted", favoriteType, map)
        }
        setFavoriteInfo(map);
    };

    const fetchWaveforms = (currPatientVisit: PatientVisit, targetDate?: Date) => {
        if (currPatientVisit) {
            const loading: any = [];
            for (let waveform of getAvailableWaveforms(currPatientVisit, filterOptions)) {
                loading[waveform] = true;
            }

            let date: Date;
            if (targetDate) {
                date = targetDate;
            } else {
                // The user has not specified a target date
                // This most likely is because this is the first time loading this patient.
                // Pick a random time between the admit time and the dispo time.
                const minTime = new Date(currPatientVisit.arrival_time).getTime();
                const maxTime = currPatientVisit.dispo_time ? new Date(currPatientVisit.dispo_time).getTime() : new Date().getTime();
                date = new Date(maxTime - 1000 * 60);
                // It is presumed if the target date was already set, then we don't need to move the annotation
                globalThis.xAnnotationMarksForNumerics = [
                    ...globalThis.xAnnotationMarksForNumerics.filter(mark => mark.type !== 'waveformMark'),
                    {
                        type: 'waveformMark',
                        from: date.getTime() / 1000,
                        to: date.getTime() / 1000 + 60,
                        label: 'Waveform',
                    }
                ];
                globalThis.xAnnotationMarksForWaveforms = [
                    ...globalThis.xAnnotationMarksForWaveforms.filter(mark => mark.type !== 'waveformMark'),
                    {
                        type: 'waveformMark',
                        from: date.getTime() / 1000,
                        to: date.getTime() / 1000 + 60,
                        label: 'Waveform',
                    }
                ];
                setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, plotWidth, currPatientVisit));
            }

            setIsLoadingWaveforms(loading);
            const available_waveforms = getAvailableWaveforms(currPatientVisit, filterOptions);

            if (currPatientVisit.archived === "SCHEMA_V1") {
                callQuery({
                    operation: ARCHIVED_WAVEFORMS,
                    csn: currPatientVisit.csn,
                    start: date.toISOString(),
                })
                .then((data: any) => {
                    if (data && data["waveforms"]) {
                        let waveformsData: any = {};
                        for (let waveform of available_waveforms) {
                            let freq = 500;
                            if (waveform === "Pleth") {
                                freq = 125;
                            } else if (waveform === "Resp") {
                                freq = 62.5;
                            }
                            if (data["waveforms"][waveform]) {
                                const timestamps = generateTimestamps(date.getTime() / 1000, 60, freq);
                                const vals = data["waveforms"][waveform];

                                console.log(`Fetched waveforms ${waveform} of shape ${vals.length}`);

                                waveformsData[waveform] = [
                                    timestamps,
                                    capWaveformVals(vals, waveform),
                                ];
                            }
                        }
                        setWaveformsData(waveformsData);
                        setWaveformOpts(available_waveforms.map((waveform: any) => getWaveformOpts(waveform, plotWidth)));
                    }

                    let loading: any = {};
                    for (let waveform of getAvailableWaveforms(currPatientVisit, filterOptions)) {
                        loading[waveform] = false;
                    }
                    setIsLoadingWaveforms(loading);
                });
            } else {
                for (let waveform of available_waveforms) {
                    readBinaryFile(`mrn=${currPatientVisit.internal_mrn}/csn=${currPatientVisit.internal_csn}/${getDatePath(date)}/${waveform}.h5`, props.tenant).then((buffer: any) => {
                        var f = new hdf5.File(buffer, "");

                        const timestamps = generateTimestamps(date.getTime() / 1000, 60, 125);
                        const vals = f.get(waveform).value;

                        console.log(`Fetched waveforms ${waveform} of shape ${vals.length}`);

                        setWaveformOpts(available_waveforms.map((waveform: any) => getWaveformOpts(waveform, plotWidth)));
                        setWaveformsData((prevState: any) => ({
                            ...prevState,
                            [waveform]: [
                                timestamps,
                                capWaveformVals(vals, waveform),
                            ],
                        }));

                        // If we hide graph if all values are the same, we may accidentally confuse users
                        //
                        // if (!areValsAreSame(vals)) {
                        //     setWaveformOpts(available_waveforms.map((waveform: any) => getWaveformOpts(waveform, plotWidth)));
                        //     setWaveformsData((prevState: any) => ({
                        //         ...prevState,
                        //         [waveform]: [
                        //             timestamps,
                        //             vals
                        //         ],
                        //     }));
                        // } else {
                        //     setWaveformsData((prevState: any) => ({
                        //         ...prevState,
                        //         [waveform]: [],
                        //     }));
                        // }
                        setIsLoadingWaveforms((prevState: any) => ({
                            ...prevState,
                            [waveform]: false,
                        }));
                    }).catch((ex: any) => {
                        console.log("Failed to get waveforms...");
                        console.log(ex);
                        setWaveformsData((prevState: any) => ({
                            ...prevState,
                            [waveform]: [],
                        }));
                        setIsLoadingWaveforms((prevState: any) => ({
                            ...prevState,
                            [waveform]: false,
                        }));
                    });
                }
            }
        }
    }

    const areValsAreSame = (vals: number[]) => {
        if (!vals || vals.length < 2) {
            return true;
        }

        const firstVal = Math.round((vals[0] + Number.EPSILON) * 100000) / 100000; // Round to five decimal places
        for (let v of vals) {
            if (Math.round((v + Number.EPSILON) * 100000) / 100000 != firstVal) {
                return false;
            }
        }
        return true;
    }

    const updateWaveformPreview = (targetDate: Date) => {
        if (patientVisit) {
            fetchWaveforms(patientVisit, targetDate);
        }
    }

    const dispatchOnElement = (elementId: string, event: any) => {
        let overlays = document.getElementById(elementId)?.getElementsByClassName("u-over");
        if (overlays && overlays.length > 0) {
            if (overlays.item && overlays.item(0) !== null) {
                overlays.item(0)!.dispatchEvent(event);
            }
        }
    }

    const doubleClickOnTimeline = () => {
        var event = new MouseEvent('dblclick', {
            'view': window,
            'bubbles': true,
            'cancelable': true
        });
        dispatchOnElement("timeline-container", event);
        dispatchOnElement("waveform-container", event);
    }

    const doubleClickOnWaveform = () => {
        if (waveformPlotRef.current && waveformOpts) {
            Object.keys(waveformsData).forEach((element: any, idx: number) => {
                if (waveformsData[element].length === 0 || waveformsData[element][0].length === 0) {
                    return;
                }
                let min: number = waveformsData[element][0][0];
                let max: number = waveformsData[element][0][0];
                waveformsData[element][0].forEach((el: number) => {
                    if (el > max) {
                        max = el;
                    }
                    if (el < min) {
                        min = el;
                    }
                });

                waveformPlotRef.current[idx].setScale("x", { min: min, max: max });
            });
        }
    }

    const zoom = (zoomIn: boolean) => {
        const uRanger = timelinePlotRef.current;

        var plots = uPlot.sync(uRanger.cursor.sync.key).plots
        if (lockTimelineZoom) {
            plots = plots.filter((plot) => plot.root.id !== "timeline");
        }

        if (plots.length > 0) {
            let plotWidth = uRanger.bbox.width;
            let currMin = plots[0].scales.x.min!;
            let currMax = plots[0].scales.x.max!;
            let currDiff = currMax - currMin;
            let currZoomAmount = currDiff * 0.3 / 2;
            if (zoomIn) {
                currMin = currMin + currZoomAmount;
                currMax = currMax - currZoomAmount;
            } else {
                currMin = currMin - currZoomAmount;
                currMax = currMax + currZoomAmount;
            }
            currMin = Math.min(currMin, currMax)
            currMax = Math.max(currMin, currMax)

            console.log("zoom set to currMin=", currMin, " currMax=", currMax)

            let left = Math.round(uRanger.valToPos(currMin, 'x'));
            let width = Math.round(uRanger.valToPos(currMax, 'x')) - left;
            let height = uRanger.bbox.height / devicePixelRatio;
            uRanger.setSelect({ left, width, height }, false);

            plots.forEach(p => {
                p.setScale("x", {
                    min: currMin,
                    max: currMax,
                })
            })
        }
    }

    const escFunction = useCallback((event: any) => {
        if (event.key === "Escape") {
            // @ts-ignore
            if (globalThis.zoomedRange && selectedTimelineMinMax && Object.keys(globalThis.zoomedRange).length === 0 && Object.keys(selectedTimelineMinMax).length === 0) {
                // The plots are not zoomed in, so we remove all selected vertical lines from plot
                handleRemoveHighlightedEvent();
            } else {
                // The plots are zoomed in, so Esc key resets zoom
                doubleClickOnTimeline();

                // Clear the zoomed range, which is a global variable that is set whenever there
                // is an active annotation. This is required as adding annotations will inherently
                // cause the plot to redraw, which will reset the zoom.
                // @ts-ignore
                globalThis.zoomedRange = {};
                setSelectedTimelineMinMax({});
            }
        }

    }, []);

    useEffect(() => {
        callQuery({
            operation: FAVORITE_LISTS_FOR_USERNAME,
        })
        .then((lists: any) => {
            lists = [
                "favorite",
                ...lists.map((list: any) => list.list_name)
            ];
            setFavoriteLists(lists);
        });
    }, []);

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);
        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    /**
     * Adds the vertical line to the numeric plot, to indicate that there is an event
     * @param x
     * @param patientVisit
     * @param xAnnotationMarks
     * @param markType
     * @param markLabel
     * @param lengthSeconds
     */
    const addAnnotationToPlots = (x: any, patientVisit: any, markType: string, markLabel: string, lengthSeconds: number = 60) => {

        console.log("addAnnotationToPlots", markType, x, markLabel, lengthSeconds);

        let preExistingMarks;
        if (markType === "waveformMark") {
            // Note: There can only ever be one mark for waveformMark
            preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => mark.type !== markType);
        } else {
            preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => (mark.label !== markLabel || mark.from !== x));
        }

        // const truncatedMarkLabel = markLabel.split(/\(|\s\d/)[0];
        const truncatedMarkLabel = markLabel;

        const newXAnnotationMarks = [
            ...preExistingMarks,
            {
                type: markType,
                from: x,
                to: x + lengthSeconds,
                label: truncatedMarkLabel,
            },
        ];
        globalThis.xAnnotationMarksForNumerics = newXAnnotationMarks;
        if (markType === "waveformMark") {
            const newXAnnotationMarksForWaveforms = [
                {
                    type: markType,
                    from: x,
                    to: x + lengthSeconds,
                    label: markLabel,
                },
            ];
            globalThis.xAnnotationMarksForWaveforms = newXAnnotationMarksForWaveforms;
        }
        setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, plotWidth, patientVisit));

        // Set timeout to ensure that the zoom is retained
        setNumericOpts((prevNumerics: uPlot.Options[]) => {
            const numerics = getAvailableNumerics(patientVisit, filterOptions);

            return numerics.map(function (numeric: any, idx: number) {
                let minMax = getCurrMinMax(patientVisit, props.tenant);
                return getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit,
                    isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef))
            });
        });
        setWaveformNumericOpts(
            PREFERRED_ECG_NUMERICS_ORDER.map((numeric: any, idx: number) => {
                let minMax = getCurrMinMax(patientVisit, props.tenant);
                return getECGNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit, true)
            })
        );

        if (markType === "eventMark") {
            setHighlightedEvents((currHighlightedEvents: any[]) => [
                ...currHighlightedEvents.filter((mark: any) => (mark.name !== markLabel || mark.x !== x)),
                {
                    x: x,
                    name: truncatedMarkLabel,
                }
            ]);
        }

        if (markType === "waveformMark") {
            updateWaveformPreview(new Date(x * 1000));
        }
    }

    const handleShowLabsModal = (show: boolean, highlightedNumericTime?: string) => {
        setShowLabsModal(show);
        if (highlightedNumericTime) {
            setLabsModalHighlightedTime(highlightedNumericTime)
        } else {
            setLabsModalHighlightedTime(null);
        }
    }

    const handleShowOtherAnnotation = (highlightedNumericTime: number) => {
        let found = false;
        const localSelected: any = [];
        for (let annotation of otherAnnotations) {
            if (getMinuteTruncatedEpoch(annotation.annotation_time) === highlightedNumericTime) {
                found = true;
                localSelected.push({
                    ...annotation
                });
            }
        }
        if (found) {
            setSelectedOtherAnnotations(localSelected);
            setShowOtherAnnotationsModal(true);
        }
    }

    const handleShowTimelineContextMenu = (e: any, show: boolean, highlightedNumericTime?: string) => {
        e.preventDefault();
        setShowContextMenu(show);
        setContextMenuPos({ x: e.clientX, y: e.clientY });
        if (highlightedNumericTime) {
            setUserAnnotationHighlightedTime(highlightedNumericTime)
        } else {
            setUserAnnotationHighlightedTime(null);
        }
    }

    const handleRemoveHighlightedEvent = (name?: string, x?: number) => {
        console.log("handleRemoveHighlightedEvent", name, x)

        let preExistingMarks: any[] = [];
        if (name) {
            // User wishes to remove a specific highlighted event
            setHighlightedEvents((currHighlightedEvents: any[]) => [
                ...currHighlightedEvents.filter((mark: any) => (mark.name !== name || mark.x !== x)),
            ]);

            preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => (mark.label !== name || mark.from !== x));
        } else {
            // User wishes to remove all highlighted events (except for waveforms)
            setHighlightedEvents((currHighlightedEvents: any[]) => []);

            preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => {
                return (mark.type === "waveformMark")
            });
        }
        const newXAnnotationMarks = [
            ...preExistingMarks,
        ];

        globalThis.xAnnotationMarksForNumerics = newXAnnotationMarks;
        setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, plotWidth, patientVisit));

        setNumericOpts((prevNumerics: uPlot.Options[]) => {
            const numerics = getAvailableNumerics(patientVisit!, filterOptions);
            return numerics.map(function (numeric: any, idx: number) {
                let minMax = getCurrMinMax(patientVisit!, props.tenant);
                return getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit,
                    isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef))
            });
        });
        setWaveformNumericOpts(
            PREFERRED_ECG_NUMERICS_ORDER.map((numeric: any, idx: number) => {
                let minMax = getCurrMinMax(patientVisit!, props.tenant);
                return getECGNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit, true)
            })
        );
    }

    const areAllNumericsEmpty = () => {
        let allNumericsEmpty = true;
        if (patientVisit) {
            getAvailableNumerics(patientVisit, filterOptions).forEach((numeric: string, idx: number) => {
                if (numericsData[numeric] && numericsData[numeric][0].length > 0) {
                    allNumericsEmpty = false;
                }
            });
        }
        return allNumericsEmpty;
    }

    const handleTimelineResizeDrag = (e: any) => {
        e.preventDefault();
        const ref = timelinePlotContainerRef.current;
        const mminStep = 50;

        const onMouseMove = (e: any) => {
            document.body.style.cursor = "pointer";

            let newHeight = e.pageY - ref.getBoundingClientRect().top;

            if (newHeight >= mminStep) {
                setTimelinePlotContainerHeight(newHeight);
                setTimelineOpts(getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, plotWidth, newHeight, patientVisit!, setSelectedTimelineMinMax, lockTimelineZoom, props.tenant));
            }
        };

        const onMouseUp = (e: any) => {
            window.removeEventListener("mousemove", onMouseMove);
            document.body.style.cursor = "auto";

            if (e && e.target && e.target.className === "plot-resize") {
                let newHeight = e.pageY - ref.getBoundingClientRect().top;
                setTimeout(() => {
                    setOverviewData(verticallySeparatePoints(overviewData, newHeight));
                }, 1000);
            }
        };

        if (timelinePlotContainerRef) {
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
        }
    };

    const handleClear = () => {
        setShowContextMenu(false);
        setShowFavoriteContextMenu(false);
    }

    return (
        <div ref={target} onClick={handleClear}>
            {
                (errorMessage) ? (
                    <>
                        <Container fluid className="ed-toolbar">
                            <Alert variant='danger'><FontAwesomeIcon icon={faExclamationCircle} /> <strong>Error: </strong> {errorMessage}</Alert>
                        </Container>
                    </>
                ) : (internalCSN && patientVisit) ? (
                    <>
                        <FilterBar
                            user={props.user}
                            ws={props.ws}
                            patientVisit={patientVisit}
                            filterOptions={filterOptions}
                            numericsData={numericsData}
                            updateFilterOptions={updateFilterOptions}
                            setShowLabsModal={handleShowLabsModal}
                            setShowMedsModal={setShowMedsModal}
                            setShowOrdersModal={setShowOrdersModal}
                            doubleClickOnTimeline={doubleClickOnTimeline}
                            zoom={zoom}
                            width={plotWidth}
                            favoriteInfo={favoriteInfo}
                            setFavoriteContextMenu={setFavoriteContextMenu}
                            setShowFavoriteContextMenu={setShowFavoriteContextMenu}
                        />

                        <Container fluid className={isDemoUser(props.user) ? "ed-patient-card-demo" : "ed-patient-card"}>
                            <Row>
                                <Col md={12}>
                                    <strong>Triage HR</strong> {Math.round(patientVisit.triage_hr)} &bull; <strong>RR</strong> {Math.round(patientVisit.triage_rr)} &bull; <strong>SpO2</strong> {patientVisit.triage_spo2} &bull; <strong>Temp</strong> {patientVisit.triage_temp} &bull; <strong>BP</strong> {Math.round(patientVisit.triage_sbp)}/{Math.round(patientVisit.triage_dbp)} &bull; <strong>Acuity</strong> {patientVisit.triage_acuity} {patientVisit.ed_dispo && (<>&bull; <strong>Dispo</strong> {patientVisit.ed_dispo}</>)}
                                </Col>
                            </Row>
                        </Container>

                        <Container fluid style={{ paddingTop: isDemoUser(props.user) ? '188px' : '60px' }}>
                            <RiskBar patientVisit={patientVisit} labs={labsData} dispoAdmitRisk={dispoAdmitRisk} setShowDispoRiskModal={setShowDispoRiskModal} showDispoRiskModal={showDispoRiskModal} tenant={props.tenant} user={props.user} />
                            <MonitorDashboard user={props.user} patientVisit={patientVisit} ws={props.ws} tenant={props.tenant} />
                            <Row className="mb-2">
                                <Col>
                                    <Row xs={1} md={1} className="g-2 mb-2">
                                        <Col>
                                            <Card>
                                                <Card.Body id="timeline-container" ref={timelinePlotContainerRef} style={{ paddingBottom: '0px', paddingLeft: '12px', paddingTop: '24px', height: `${timelinePlotContainerHeight}px` }}>
                                                    <div style={{ position: 'absolute', left: '4px', top: '4px', width: '370px', fontSize: '0.8rem' }}>
                                                        {
                                                            lockTimelineZoom ? (
                                                                <Button variant="secondary"  size='sm' className="btn-xsm" onClick={() => { setLockTimelineZoom(!lockTimelineZoom); resetZoom(patientVisit, false) }}>Unlock Zoom</Button>
                                                            ) : (
                                                                <Button variant="outline-secondary" size='sm' className="btn-xsm" onClick={() => { setLockTimelineZoom(!lockTimelineZoom); resetZoom(patientVisit, true) }}>Lock Zoom</Button>
                                                            )
                                                        }
                                                    </div>
                                                    <div className="text-right" style={{ position: 'absolute', right: '16px', top: '4px', width: '620px' }}>
                                                        <div className="text-right float-end plot-indicator-container">
                                                            <div className='plot-indicator-container-item'>
                                                                <span className="plot-indicator-container-icon indicator-normal" style={{ fontSize: '1rem' }}>&#9650;</span>{' '}<span className="plot-indicator-container-desc">Lab Collection</span>
                                                            </div>

                                                            <div className='plot-indicator-container-item'>
                                                                <span className="plot-indicator-container-icon indicator-normal"><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Medication Administration</span>
                                                            </div>

                                                            <div className='plot-indicator-container-item'>
                                                                <span className="plot-indicator-container-icon indicator-report"><FontAwesomeIcon icon={faCircle} /></span>{' '}
                                                                <OverlayTrigger
                                                                    placement="left"
                                                                    overlay={
                                                                        <Tooltip>
                                                                            Includes radiology imaging reports and other text documents
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="plot-indicator-container-desc">Reports</span>
                                                                </OverlayTrigger>
                                                            </div>

                                                            <div className='plot-indicator-container-item'>
                                                                <span className="plot-indicator-container-icon indicator-user"><FontAwesomeIcon icon={faCircle} /></span>{' '}
                                                                <OverlayTrigger
                                                                    placement="left"
                                                                    overlay={
                                                                        <Tooltip>
                                                                            Right-click on timeline to add annotation
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="plot-indicator-container-desc">User Annotation</span>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {
                                                        isLoadingOverview ? (
                                                            <>
                                                                <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                    <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                                                </p>
                                                            </>
                                                        ) : timelineOpts && overviewData[0] && overviewData[0].length >= 0 ? (
                                                            <>
                                                                <UplotReact
                                                                    // @ts-ignore
                                                                    options={timelineOpts}
                                                                    // TODO: Use meds, labs, etc.
                                                                    data={overviewData as uPlot.AlignedData}
                                                                    onCreate={(uRanger: uPlot) => {
                                                                        timelinePlotRef.current = uRanger;
                                                                        const sel = uRanger.root.querySelector(".u-over");
                                                                        on("dblclick", sel, (e: any) => {
                                                                            // This ensures that we reset the scales of our chart after we have let the double click event propagate.
                                                                            setTimeout(() => {
                                                                                resetZoom();
                                                                            }, 0);
                                                                        });
                                                                        on("mousedown", sel, (e: any) => {
                                                                            bindMove(uRanger, e, (e: any) => update(uRanger, lft0 + (e.clientX - x0), wid0));
                                                                        });
                                                                    }}
                                                                    resetScales={false}
                                                                />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                    <FontAwesomeIcon icon={faExclamationCircle} /> No Medications and Labs Found
                                                                </p>
                                                            </>
                                                        )
                                                    }
                                                </Card.Body>

                                                <div
                                                    className="plot-resize"
                                                    onMouseDown={(e) => handleTimelineResizeDrag(e)}
                                                >
                                                </div>
                                            </Card>
                                        </Col>
                                    </Row>
                                    {/* <DispoRisk tenant={props.tenant} showModal={showDispoRiskModal} patientVisit={patientVisit} featureImportance={featureImportance} timestamps={timestamps} setShowModal={setShowDispoRiskModal} dispoAdmitRiskTrend={dispoAdmitRiskTrend} plotWidthRef={plotWidthRef} /> */}

                                    <Row xs={1} md={1} className="g-2" id="plot-container">
                                        <Col>
                                            <Card>
                                                <Card.Body style={{ paddingBottom: '0px' }}>
                                                    {/* If we do not have a min height, every time the highlighted event is added it will push the plots below causing them to rerender */}
                                                    <Row style={{ minHeight: '28px' }}>
                                                        <Col md={9}>
                                                            <span style={{ marginRight: '16px' }}>
                                                                <FontAwesomeIcon icon={faChartLine} /> <strong>Numerics</strong>
                                                            </span>
                                                            {
                                                                highlightedEvents.map((highlightedEvent: any) => (
                                                                    <ButtonGroup key={highlightedEvent.name + highlightedEvent.id} style={{ marginRight: '4px' }}>
                                                                        <Button variant="secondary" className="btn-xsm">{highlightedEvent.name}</Button>
                                                                        <Button variant="secondary" className="btn-xsm" onClick={() => handleRemoveHighlightedEvent(highlightedEvent.name, highlightedEvent.x)}><FontAwesomeIcon icon={faClose} /></Button>
                                                                    </ButtonGroup>
                                                                ))
                                                            }
                                                        </Col>
                                                        <Col md={3}>
                                                            <div className="text-right float-end plot-indicator-container">
                                                                {
                                                                    FILTER_REFERENCE_RANGE_OPTIONS[filterOptions["reference"]].id !== "none" && (
                                                                        <div className='plot-indicator-container-item'>
                                                                            <span className="plot-indicator-container-icon indicator-abnormal"><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Out of Range</span>
                                                                        </div>
                                                                    )
                                                                }

                                                                <div className='plot-indicator-container-item'>
                                                                    <span className="plot-indicator-container-icon indicator-normal"><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Charted</span>
                                                                </div>

                                                                <div className='plot-indicator-container-item'>
                                                                    <span className="plot-indicator-container-icon indicator-normal"><FontAwesomeIcon fontWeight={800} icon={faMinus} /></span>{' '}<span className="plot-indicator-container-desc">Monitor {lastUpdatedTime && !patientVisit.departure_time && <>(updated {getRelativeDate(lastUpdatedTime)})</>}</span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    {
                                                        !isLoadingNumerics && areAllNumericsEmpty() && (
                                                            <>
                                                                <p style={{ textAlign: 'center', marginTop: '48px', fontSize: '64px', color: '#EEE' }}>
                                                                    <FontAwesomeIcon icon={faRocket} />
                                                                </p>
                                                                <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px', color: '#EEE' }}>
                                                                    No data found
                                                                </p>
                                                            </>
                                                        )
                                                    }
                                                    {getAvailableNumerics(patientVisit, filterOptions).map((numeric: string, idx: number) => (
                                                        <div key={numeric}>
                                                            {
                                                                isLoadingNumerics ? (
                                                                    <>
                                                                        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{numeric}</span>
                                                                        <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                            <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                                                        </p>
                                                                    </>
                                                                ) : (numericsHidden[numeric] && numericsData[numeric] && numericsData[numeric][0].length > 0) ? (
                                                                    <>
                                                                        <div style={{ position: 'relative', height: '24px', marginTop: '12px' }}>
                                                                            <div style={{ position: 'absolute', top: '-4px', width: '100%' }}>
                                                                                <hr />
                                                                            </div>
                                                                            <p style={{ backgroundColor: 'white', paddingRight: '12px', fontSize: '0.7rem', position: 'absolute', left: '0px', top: '2px', height: '12px' }}>
                                                                                <strong>{numeric}</strong>
                                                                            </p>
                                                                            <div style={{ position: 'absolute', left: '-12px', top: '0px', cursor: 'pointer' }}>
                                                                                <FontAwesomeIcon icon={faCaretRight} onClick={() => { handleNumericsHiddenChange({ ...numericsHidden, [numeric]: false }) }} />
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                ) : numericOpts[idx] && numericsData[numeric] && numericsData[numeric][0].length > 0 ? (
                                                                    <>
                                                                        <div style={{ position: 'relative' }}>
                                                                            <p style={{ backgroundColor: 'white', transform: 'translateY(50%) rotate(-90deg)', marginTop: '6px', marginBottom: '0px', transformOrigin: 'left center', fontSize: '0.7rem', position: 'absolute', left: '10px', top: isDemoUser(props.user) ? '64px' : '64px' }}>
                                                                                <strong>{numeric}</strong>
                                                                            </p>
                                                                            <div style={{ marginLeft: '16px' }}>
                                                                                <UplotReact
                                                                                    options={numericOpts[idx]}
                                                                                    data={numeric === "Temp" ? convertToTempUnit(numericsData[numeric]!, tempUnit) : (numericsData[numeric] as uPlot.AlignedData)}
                                                                                    onCreate={(uRanger: uPlot) => {
                                                                                        const sel = uRanger.root.querySelector(".u-over");
                                                                                        on("dblclick", sel, (e: any) => {
                                                                                            // This ensures that we reset the scales of our chart after we have let the double click event propagate.
                                                                                            setTimeout(() => {
                                                                                                resetZoom();
                                                                                            }, 0);
                                                                                        });
                                                                                    }}
                                                                                    resetScales={false}
                                                                                />
                                                                            </div>
                                                                            {
                                                                                numeric === "Temp" && (
                                                                                    <div style={{ position: 'absolute', left: '-7px', bottom: '-9px', cursor: 'pointer', fontSize: '0.8rem', backgroundColor: 'white', borderRadius: '4px', padding: '4px' }}>
                                                                                        {
                                                                                            tempUnit === "F" ? (
                                                                                                <><a onClick={() => {
                                                                                                    setTempUnit('C');
                                                                                                    setTimeout(() => {
                                                                                                        resetZoom();
                                                                                                    }, 0);
                                                                                                }}>C</a> | <a onClick={() => {
                                                                                                    setTempUnit('F');
                                                                                                    setTimeout(() => {
                                                                                                        resetZoom();
                                                                                                    }, 0);
                                                                                                }}><strong>F</strong></a></>
                                                                                            ) : (
                                                                                                <><a onClick={() => {
                                                                                                    setTempUnit('C');
                                                                                                    setTimeout(() => {
                                                                                                        resetZoom();
                                                                                                    }, 0);
                                                                                                }}><strong>C</strong></a> | <a onClick={() => {
                                                                                                    setTempUnit('F');
                                                                                                    setTimeout(() => {
                                                                                                        resetZoom();
                                                                                                    }, 0);
                                                                                                }}>F</a></>
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                )
                                                                            }
                                                                            <div style={{ position: 'absolute', left: '-12px', top: isDemoUser(props.user) ? '64px' : '64px', cursor: 'pointer' }}>
                                                                                <FontAwesomeIcon icon={faCaretDown} onClick={() => { handleNumericsHiddenChange({ ...numericsHidden, [numeric]: true }) }} />
                                                                            </div>
                                                                            {
                                                                                numericsRefRangeData[numeric] && (
                                                                                    <div style={{ position: 'absolute', right: '12px', top: '6px', cursor: 'pointer' }} className="u-mark-ref-range">
                                                                                        {numericsRefRangeData[numeric]}
                                                                                    </div>
                                                                                )
                                                                            }
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <></>
                                                                )
                                                            }
                                                        </div>
                                                    ))}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {
                                        (isLoadingNumerics || PREFERRED_ECG_NUMERICS_ORDER.some(numeric => waveformNumericsData[numeric] && waveformNumericsData[numeric][0].length > 0)) && (
                                            <Row xs={1} md={1} className="g-2 mt-1" id="ecg-numerics-container">
                                                <Col>
                                                    <Card>
                                                        <Card.Body style={{ paddingBottom: '0px' }}>
                                                            {/* If we do not have a min height, every time the highlighted event is added it will push the plots below causing them to rerender */}
                                                            <Row style={{ minHeight: '28px' }}>
                                                                <Col md={9}>
                                                                    <span style={{ marginRight: '16px' }}>
                                                                        <FontAwesomeIcon icon={faChartLine} /> <strong>ECG Numerics</strong>
                                                                    </span>
                                                                    {
                                                                        highlightedEvents.map((highlightedEvent: any) => (
                                                                            <ButtonGroup key={highlightedEvent.name + highlightedEvent.id} style={{ marginRight: '4px' }}>
                                                                                <Button variant="secondary" className="btn-xsm">{highlightedEvent.name}</Button>
                                                                                <Button variant="secondary" className="btn-xsm" onClick={() => handleRemoveHighlightedEvent(highlightedEvent.name, highlightedEvent.x)}><FontAwesomeIcon icon={faClose} /></Button>
                                                                            </ButtonGroup>
                                                                        ))
                                                                    }
                                                                </Col>
                                                                <Col md={3}>
                                                                    <div className="text-right float-end plot-indicator-container">
                                                                        <div className='plot-indicator-container-item'>
                                                                            <span className="plot-indicator-container-icon indicator-normal"><FontAwesomeIcon fontWeight={800} icon={faMinus} /></span>{' '}<span className="plot-indicator-container-desc">Monitor {lastUpdatedTime && !patientVisit.departure_time && <>(updated {getRelativeDate(lastUpdatedTime)})</>}</span>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                            {PREFERRED_ECG_NUMERICS_ORDER.map((numeric: string, idx: number) => (
                                                                <div key={numeric}>
                                                                    {
                                                                        isLoadingNumerics ? (
                                                                            <>
                                                                                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{numeric}</span>
                                                                                <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                                    <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                                                                </p>
                                                                            </>
                                                                        ) : (numericsHidden[numeric] && waveformNumericsData[numeric] && waveformNumericsData[numeric][0].length > 0) ? (
                                                                            <>
                                                                                <div style={{ position: 'relative', height: '24px', marginTop: '12px' }}>
                                                                                    <div style={{ position: 'absolute', top: '-4px', width: '100%' }}>
                                                                                        <hr />
                                                                                    </div>
                                                                                    <p style={{ backgroundColor: 'white', paddingRight: '12px', fontSize: '0.7rem', position: 'absolute', left: '0px', top: '2px', height: '12px' }}>
                                                                                        <strong>{numeric}</strong>
                                                                                    </p>
                                                                                    <div style={{ position: 'absolute', left: '-12px', top: '0px', cursor: 'pointer' }}>
                                                                                        <FontAwesomeIcon icon={faCaretRight} onClick={() => { handleNumericsHiddenChange({ ...numericsHidden, [numeric]: false }) }} />
                                                                                    </div>
                                                                                </div>
                                                                            </>
                                                                        ) : waveformNumericsOpts[idx] && waveformNumericsData[numeric] && waveformNumericsData[numeric][0].length > 0 ? (
                                                                            <>
                                                                                <div style={{ position: 'relative' }}>
                                                                                    <p style={{ backgroundColor: 'white', transform: 'translateY(50%) rotate(-90deg)', marginTop: '6px', marginBottom: '0px', transformOrigin: 'left center', fontSize: '0.7rem', position: 'absolute', left: '10px', top: isDemoUser(props.user) ? '64px' : '64px' }}>
                                                                                        <strong>{numeric}</strong>
                                                                                    </p>
                                                                                    <div style={{ marginLeft: '16px' }}>
                                                                                        <UplotReact
                                                                                            options={waveformNumericsOpts[idx]}
                                                                                            data={waveformNumericsData[numeric] as uPlot.AlignedData}
                                                                                            onCreate={(uRanger: uPlot) => {
                                                                                                const sel = uRanger.root.querySelector(".u-over");
                                                                                                on("dblclick", sel, (e: any) => {
                                                                                                    // This ensures that we reset the scales of our chart after we have let the double click event propagate.
                                                                                                    setTimeout(() => {
                                                                                                        resetZoom();
                                                                                                    }, 0);
                                                                                                });
                                                                                            }}
                                                                                            resetScales={false}
                                                                                        />
                                                                                    </div>
                                                                                    <div style={{ position: 'absolute', left: '-12px', top: isDemoUser(props.user) ? '64px' : '64px', cursor: 'pointer' }}>
                                                                                        <FontAwesomeIcon icon={faCaretDown} onClick={() => { handleNumericsHiddenChange({ ...numericsHidden, [numeric]: true }) }} />
                                                                                    </div>
                                                                                    {
                                                                                        numericsRefRangeData[numeric] && (
                                                                                            <div style={{ position: 'absolute', right: '12px', top: '6px', cursor: 'pointer' }} className="u-mark-ref-range">
                                                                                                {numericsRefRangeData[numeric]}
                                                                                            </div>
                                                                                        )
                                                                                    }
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <></>
                                                                        )
                                                                    }
                                                                </div>
                                                            ))}
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        )
                                    }
                                    <ECGRhythmTimeline waveformAvailabilityData={waveformAvailabilityData} numericsData={numericsData} setHighlightRange={setHighlightRange} addAnnotationToPlots={addAnnotationToPlots} patientVisit={patientVisit} />
                                    <Row xs={1} md={1} className="g-2 mt-1">
                                        <Col>
                                            <Card>
                                                <Card.Body id="waveform-container" className="waveform-availability" style={{ paddingBottom: '0px' }}>
                                                    <Row>
                                                        <Col md={6}>
                                                            <FontAwesomeIcon icon={faWaveSquare} /> <strong>Waveforms</strong>
                                                        </Col>

                                                        <Col md={6}>
                                                            <div className="text-right float-end plot-indicator-container">
                                                                <div className='plot-indicator-container-item'>
                                                                    <span className="plot-indicator-container-icon indicator-abnormal"><FontAwesomeIcon icon={faCircle} /></span>{' '}

                                                                    <span className="plot-indicator-container-desc plot-indicator-container-desc-dropdown" onClick={() => setFilterWaveformAnnotationShow(true)}>
                                                                        {uniqueECGClassifications.length > 0 ? uniqueECGClassifications[filterWaveformOptions["waveformAnnotation"]].name : ""}{' '}<FontAwesomeIcon icon={faCaretDown} />
                                                                    </span>

                                                                    {filterWaveformAnnotationShow && (
                                                                        <WaveformAnnotationSingleSelectMenu
                                                                            selectedItem={filterWaveformOptions["waveformAnnotation"]}
                                                                            option="waveformAnnotation"
                                                                            updateFilterOptions={updateFilterWaveformOptions}
                                                                            availableItems={uniqueECGClassifications}
                                                                            setShow={setFilterWaveformAnnotationShow}
                                                                        />
                                                                    )}
                                                                </div>

                                                                <div className='plot-indicator-container-item'>
                                                                    {patientVisit && !patientVisit.archived && (
                                                                        <>
                                                                            <span className="plot-indicator-container-icon indicator-normal"><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Sufficient Quality</span>{' '}
                                                                        </>
                                                                    )}
                                                                    <span className={patientVisit && patientVisit.archived ? "plot-indicator-container-icon indicator-normal" : "plot-indicator-container-icon indicator-insufficient"}><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">{patientVisit && patientVisit.archived ? "Available" : "Insufficient Quality"}</span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    {
                                                        isLoadingAnnotations ? (
                                                            <>
                                                                <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                    <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                                                </p>
                                                            </>
                                                        ) : waveformAvailabilityOpts && waveformAvailabilityData[0].length > 0 ? (
                                                            <>
                                                                <UplotReact
                                                                    // @ts-ignore
                                                                    options={waveformAvailabilityOpts}
                                                                    // TODO: Use meds, labs, etc.
                                                                    data={waveformAvailabilityData as uPlot.AlignedData}
                                                                />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p className="not-found-text">
                                                                    <FontAwesomeIcon icon={faExclamationCircle} /> Annotations not available.
                                                                </p>
                                                                <hr />
                                                            </>
                                                        )
                                                    }
                                                    <hr />
                                                    {getAvailableWaveforms(patientVisit, filterOptions).map((waveform: string, idx: number) => (
                                                        <div key={waveform}>
                                                            {
                                                                waveform === "II" && (
                                                                    <Row>
                                                                        <Col md={6} className="plot-indicator-container">
                                                                            <div>
                                                                                <span className="plot-tip">
                                                                                    <strong>Tip</strong>:{' '}Drag To Zoom In
                                                                                </span>
                                                                            </div>
                                                                        </Col>

                                                                        <Col md={6}>
                                                                            <div className="text-right float-end plot-indicator-container">

                                                                                <div className='plot-indicator-container-item'>
                                                                                    <Button variant="outline-secondary" size='sm' onClick={doubleClickOnWaveform}>Reset Zoom</Button>
                                                                                    {/* <ButtonGroup aria-label="Swap ECG View">
                                                                                        <Button variant="secondary" size='sm'>Full-Length</Button>
                                                                                        <Button variant="outline-secondary" size='sm'>ECG Strip</Button>
                                                                                    </ButtonGroup> */}
                                                                                </div>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                )
                                                            }
                                                            {
                                                                isLoadingWaveforms[waveform] ? (
                                                                    <>
                                                                        <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                            <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                                                        </p>
                                                                    </>
                                                                ) : waveformOpts[idx] && waveformsData[waveform] && waveformsData[waveform][0] && waveformsData[waveform][0].length > 0 ? (
                                                                    <>
                                                                        <UplotReact
                                                                            options={waveformOpts[idx]}
                                                                            data={waveformsData[waveform] as uPlot.AlignedData}
                                                                            onCreate={(uRanger: uPlot) => {
                                                                                waveformPlotRef.current[idx] = uRanger;
                                                                            }}
                                                                            resetScales={false}
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <p className="not-found-text">
                                                                            <FontAwesomeIcon icon={faExclamationCircle} /> <strong>{waveform}</strong> not available. Try selecting a different time period above.
                                                                        </p>
                                                                        <hr />
                                                                    </>
                                                                )
                                                            }
                                                        </div>
                                                    ))}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                            <Row style={{ marginBottom: '4px' }}>
                                <LabsModal showModal={showLabsModal} setShowModal={handleShowLabsModal} labs={labsData} patientVisit={patientVisit} addAnnotationToPlots={addAnnotationToPlots} highlightedLabTime={labsModalHighlightedTime} />
                                <MedsModal showModal={showMedsModal} setShowModal={setShowMedsModal} meds={medsData} patientVisit={patientVisit} addAnnotationToPlots={addAnnotationToPlots} />
                                <OrdersModal showModal={showOrdersModal} setShowModal={setShowOrdersModal} patientVisit={patientVisit} addAnnotationToPlots={addAnnotationToPlots} />

                                <OtherAnnotationModal showModal={showOtherAnnotationsModal} setShowModal={setShowOtherAnnotationsModal} selectedAnnotations={selectedOtherAnnotations} patientVisit={patientVisit} addAnnotationToPlots={addAnnotationToPlots} />
                                <UserAnnotationModal show={showUserAnnotationModal} setShow={setShowUserAnnotationModal} patientVisit={patientVisit} userAnnotations={userAnnotations} setUserAnnotations={setUserAnnotations} userAnnotationHighlightedTime={userAnnotationHighlightedTime} fetchChartAndAnnotations={fetchChartAndAnnotations} />
                                <TimelineContextMenu show={showContextMenu} setShow={setShowContextMenu} position={contextMenuPos} setShowUserAnnotationModal={setShowUserAnnotationModal} resetZoom={doubleClickOnTimeline} />
                                <FavoriteContextMenu show={showFavoriteContextMenu} contextMenu={favoriteContextMenu} lists={favoriteLists} setShow={setShowFavoriteContextMenu} setShowNewList={setShowNewList} updateCsnToFavorites={updateCsnToFavorites} />
                                <FavoriteNewListModal show={showNewList} setShow={setShowNewList} lists={favoriteLists} setLists={setFavoriteLists} contextMenu={favoriteContextMenu} updateCsnToFavorites={updateCsnToFavorites} />
                            </Row>
                        </Container>
                    </>
                ) : (internalCSN && !patientVisit) ? (
                    <LoadingPatient />
                ) : (
                    <Welcome user={props.user} tenant={props.tenant} plotWidth={plotWidth} />
                )
            }
        </div>
    );
};
